import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Topbar } from '@/components/Topbar';
import { SkipLink } from '@/components/SkipLink';
import { NavShell } from '@/components/NavShell';
import { TicketRow } from '@/components/TicketRow';
import { ExamHQ } from '@/components/cockpit/ExamHQ';
import { VoucherTable } from '@/components/VoucherTable';
import { Board } from '@/screens/Board';
import { SprintDayHeader } from '@/components/SprintDayHeader';
import { OverviewBento } from '@/components/OverviewBento';
import { deriveSkillsFromProgress } from '@/lib/derivedSkills';
import { computeStreakDays, deriveNearestMilestone } from '@/lib/overviewData';
import { activeSprint } from '@/lib/sprint-progression';
import { useProgress } from '@/contexts/ProgressContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { sprintById, SPRINTS } from '@shared/data/sprints';
import { ticketsBySprint, allTickets } from '@shared/data/tickets';
import { quizBankExists } from '@shared/data/quiz-bank-ids';
import { isSprintLockedForUser, roleOf, canPeekSprint } from '@/lib/access';
import { useAuthContextOptional } from '@/contexts/AuthContext';
import type { HomeworkStatus } from '@/types';

interface SprintProps {
  sprintId: string;
  /**
   * Read-only DEMO view · slot_progression_plan_quiz Phase 3 (2 JUN 2026).
   * Trial «заглянуть» into a locked sprint: bypasses the lock→Dashboard redirect
   * and shows a banner. Progress writes are no-op'd globally via ProgressContext
   * demoMode (set by App in lockstep with this prop). Passed as a prop (not read
   * from context) so the lock-redirect sees it synchronously on first render.
   */
  demo?: boolean;
  onLogo: () => void;
  onTicket: (id: string) => void;
  onDashboard: () => void;
  onFinalTest?: (id: string) => void;
  onReview?: (id: string) => void;
  onOpenGlossary?: () => void;
  /** slot_s06_rework W5: глоссарий-тренажёр из «штаба» (ExamHQ · S06). */
  onOpenFlashcards?: () => void;
  onStartMock?: (slot: 'mock1' | 'mock2') => void;
  /**
   * RISO Phase C (16 MAY 2026) · optional nav callbacks for NavShell routing.
   * All fallback to onDashboard when not provided · App.tsx wires gradually
   * as Phase D screens (Sprints/Progress) and Phase G routing finalize.
   */
  onSprints?: () => void;
  onPeek?: (sprintId: string) => void;
  onProgress?: () => void;
  /**
   * SBM merge (18 MAY 2026 · slot_sprint_board_merge): `onBoard` prop dropped.
   * View toggle inside Sprint screen handles board access · standalone /board
   * route retired (App.tsx SBM.4 cleanup) · NavShell 'board' key no longer
   * emitted after Sidebar NAV_ITEMS reshape.
   */
  onBuddy?: () => void;
  /** Fallback for nav keys not handled here (practice/portfolio · App dispatcher). */
  onNavFallback?: (key: string) => void;
}

// Backlog tab dropped 2 MAY 2026 (Bug 4) · subview prop preserved as a single
// 'tickets' constant for future re-introduction если понадобится (e.g. spec'd
// per-sprint backlog tasks из ATELIER_TASKS).
const FILTERS = [
  { k: 'all', label: 'Все' },
  { k: 'open', label: 'Открытые' },
  { k: 'done', label: 'Закрытые' },
  { k: 'master', label: 'Master ★' },
] as const;

type FilterKey = (typeof FILTERS)[number]['k'];

// ─── SBM merge (18 MAY 2026 · slot_sprint_board_merge) ────────────────────
// View toggle state · single Sprint screen replaces former Sprint+Board split.
// localStorage key persists globally (Genie pick OQ-3 · 18 MAY).
// Default first-visit fallback: uniform 'board' для всех спринтов (Genie
// reversal of OQ-2 · post-review 18 MAY 2026 «список сейчас дефолтный — давай
// сделаем доску дефолтной»). Doesn't affect saved preference (still respected).
// Icons: ☰ список / ⊞ доска (Genie pick OQ-1).
// `sprintId` arg preserved для backward compat + future per-sprint heuristics
// (currently unused · sprintById lookup deferred).
// slot_board_overview (10 JUL · Genie): третий режим доски — «обзор» (bento-дашборд
// прямо в спринте, вкладкой рядом со список/доска). Экран-«Обзор» в навигации остаётся.
type ViewMode = 'list' | 'board' | 'overview';
const VIEW_MODE_KEY = 'qastudio.v1.sprint.viewMode';

export function loadInitialViewMode(_sprintId: string): ViewMode {
  if (typeof window === 'undefined') return 'board';
  try {
    const saved = window.localStorage.getItem(VIEW_MODE_KEY);
    if (saved === 'list' || saved === 'board' || saved === 'overview') return saved;
  } catch {
    /* localStorage disabled (Safari private mode etc.) → fall through */
  }
  // slot_t00_ftue_rewrite (7 JUN 2026 · Genie): на телефонах и планшетах
  // (≤1024px) дефолт — список: три канбан-колонки на узком экране нечитаемы.
  // Явный выбор пользователя (saved выше) всегда важнее. Уточняет uniform
  // 'board' default из SBM.7 — тот остаётся для десктопа.
  return window.innerWidth <= 1024 ? 'list' : 'board';
}

/**
 * Format «started X · day N of N» counter from earliest ticket.startedAt
 * для этого спринта (если есть). Без data — graceful fallback к «спринт активен».
 *
 * Day-counter derivation: progress.tickets[*].startedAt MIN → today delta.
 * Sprint-level startedAt поле в SprintProgress пока нет (cross-flag → Chat 2b).
 */
function formatDayCounter(startedISO: string | null, now: Date = new Date()): {
  startedLabel: string;
  dayLabel: string;
} {
  if (!startedISO) return { startedLabel: '', dayLabel: '' };
  const started = new Date(startedISO);
  const diffMs = now.getTime() - started.getTime();
  const days = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1);
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const startedLabel = `started ${started.getDate()} ${months[started.getMonth()]}`;
  // sprint length эвристика: 7-day target per ROLE «день 5 of 7» pattern
  const dayLabel = `день ${days} of 7`;
  return { startedLabel, dayLabel };
}

export function Sprint({
  sprintId,
  demo = false,
  onLogo,
  onTicket,
  onDashboard,
  onFinalTest,
  onReview,
  onOpenGlossary,
  onOpenFlashcards,
  onStartMock,
  onSprints,
  onPeek,
  onProgress,
  onBuddy,
  onNavFallback,
}: SprintProps) {
  const { progress, setSprintProgress } = useProgress();
  const sprint = sprintById(sprintId);
  const tickets = useMemo(() => ticketsBySprint(sprintId), [sprintId]);
  // slot_board_overview (10 JUL): props для bento-дашборда во вкладке «обзор».
  // Курс-уровень сводка (не привязана к просматриваемому спринту) — берём активный
  // спринт курса. Хук ДО early-return `if (!sprint)` (rules-of-hooks).
  const overviewData = useMemo(() => {
    const now = Date.now();
    const currentSprintOrd = activeSprint(progress).ord;
    return {
      currentSprintOrd,
      streakDays: computeStreakDays(progress.tickets, now),
      skills: deriveSkillsFromProgress(progress, allTickets()),
      nearestMilestone: deriveNearestMilestone(progress, SPRINTS, currentSprintOrd),
    };
  }, [progress]);
  const hasQuiz = quizBankExists(sprint?.finalTestBankId);
  const sprintProgress = progress.sprints[sprintId];
  const finalTestPassed = (sprintProgress?.finalTestScore ?? 0) >= 78;
  const isS06 = sprintId === 'S06';

  // Final test gate: all Required tickets (everything except mission) must be done.
  // Per Bug 4 spec · 1 MAY 2026 · mission-type tickets stay optional.
  const requiredTickets = useMemo(
    () => tickets.filter((t) => t.type !== 'mission'),
    [tickets],
  );
  const requiredDone = useMemo(
    () => requiredTickets.every((t) => progress.tickets[t.id]?.status === 'done'),
    [requiredTickets, progress.tickets],
  );
  const finalTestUnlocked = hasQuiz && requiredDone;

  // slot_finaltest_ux #4 · sprint-completion → final-test nudge.
  // Genie 2026-06-02: the FIRST time a sprint is fully done show a modal; on
  // every later visit/refresh downgrade to a non-blocking toast («окно после
  // каждого рефреша раздражает»). «Seen» is persisted per sprint (survives
  // refresh); cleared by logout purge → a fresh login shows the modal once more.
  const sprintComplete = !!onFinalTest && finalTestUnlocked && !finalTestPassed;
  // Key bumped to .v2 (8 JUN 2026): the old flag was burned at modal *show* time
  // (see effect below) — a sprint completed in any earlier session marked the
  // nudge «seen» forever, so the modal never re-appeared (S02/S03 «окно не
  // всплывает» bug). Bumping the key resets the stale flags. Stays in the
  // `qastudio.v1.*` namespace so logout-purge still clears it.
  const [nudgeSeen, setNudgeSeen] = useLocalStorage<Record<string, boolean>>(
    'qastudio.v1.sprintTestNudgeSeen.v2',
    {},
  );
  // 'pending' → not decided yet · 'modal'/'toast' → showing · 'off' → dismissed.
  const [nudge, setNudge] = useState<'pending' | 'modal' | 'toast' | 'off'>('pending');
  useEffect(() => {
    setNudge('pending');
  }, [sprintId]);
  useEffect(() => {
    if (nudge !== 'pending') return; // decide once per sprint visit
    if (!sprintComplete) return; // wait until the sprint is actually complete
    // First view of a completed sprint → modal; once the student has actually
    // dismissed it → toast on later visits. «Seen» is marked ONLY on explicit
    // dismiss (dismissTestNudge) — NOT here at show time. Marking it here burned
    // the one-time show even when the window never reached the screen, so it
    // never re-appeared (S02/S03 bug · 8 JUN 2026).
    setNudge(nudgeSeen[sprintId] ? 'toast' : 'modal');
  }, [nudge, sprintComplete, sprintId, nudgeSeen]);

  /** Dismiss the completion nudge AND persist «seen» (so later visits → toast). */
  const dismissTestNudge = () => {
    setNudge('off');
    if (!nudgeSeen[sprintId]) setNudgeSeen({ ...nudgeSeen, [sprintId]: true });
  };
  const showTestModal = sprintComplete && nudge === 'modal';
  const showTestToast = sprintComplete && nudge === 'toast';

  const [filter, setFilter] = useState<FilterKey>('all');

  // SBM merge · viewMode state · saved global preference OR block-based fallback.
  // loadInitialViewMode читает localStorage at mount · subsequent useEffect
  // persists changes. Initial render uses synchronous lookup (no flash).
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadInitialViewMode(sprintId));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      /* localStorage disabled → silently no-op */
    }
  }, [viewMode]);

  // Bug 3 guard · если спринт заблокирован (предыдущий не закрыт ИЛИ trial-кап
  // S05+) — назад в Dashboard. Role-aware (slot_progression_plan_quiz Phase 2):
  // trial не может реально открыть S05+ (только demo · Phase 3). Вне провайдера
  // (screen-тесты) роль → 'trial' по умолчанию (поведение в S01-S04 не меняется).
  const authCtx = useAuthContextOptional();
  const role = roleOf(authCtx?.user);
  const locked = isSprintLockedForUser(progress, sprintId, role);
  useEffect(() => {
    // In read-only demo we intentionally render a locked sprint (peek) — skip the
    // bounce. Progress writes are already no-op'd via ProgressContext demoMode.
    if (locked && !demo) onDashboard();
  }, [locked, demo, onDashboard]);

  // RISO Phase C · NavShell click routing · pass-through к existing callbacks.
  // Unwired keys fall back к onDashboard (Phase G finalizes routing).
  const handleNav = useCallback(
    (key: string) => {
      switch (key) {
        case 'dashboard':
          onDashboard();
          break;
        case 'sprint':
          // Already here · no-op.
          break;
        case 'sprints':
          (onSprints ?? onDashboard)();
          break;
        case 'progress':
          (onProgress ?? onDashboard)();
          break;
        case 'buddy':
          if (onBuddy) onBuddy();
          break;
        case 'atelier':
          // Handled internally by NavShell (overlay toggle).
          break;
        // SBM merge (18 MAY 2026): 'board' case retired · Sidebar NAV_ITEMS no
        // longer emits 'board' key (item removed; 'sprint' renamed к Доска).
        default:
          onNavFallback?.(key);
          break;
      }
    },
    [onDashboard, onSprints, onProgress, onBuddy, onNavFallback],
  );

  // RISO Phase C · derived data · useMemo'd BEFORE early return per react-hooks/rules-of-hooks.
  // Day counter · CHAT_2B Q-1 (Genie adopt 16 MAY) · prefer authoritative
  // `progress.sprints[id].startedAt` · fallback к MIN(ticket.startedAt) для
  // backward compat (older saves pre-Q-1 lack sprint-level field).
  const earliestStarted = useMemo(() => {
    if (sprintProgress?.startedAt) return sprintProgress.startedAt;
    const starts = tickets
      .map((t) => progress.tickets[t.id]?.startedAt)
      .filter((v): v is string => typeof v === 'string')
      .sort();
    return starts[0] ?? null;
  }, [sprintProgress?.startedAt, tickets, progress.tickets]);

  // CHAT_2B Q-1 · ensure sprint.startedAt is set on first open per sprint (Sprint
  // screen mount). One-shot per sprintId · idempotent · LWW conflict resolution
  // applies per ADR-007. Skipped if already set (preserves earliest timestamp).
  useEffect(() => {
    if (sprintProgress?.startedAt) return;
    if (locked) return;
    setSprintProgress(sprintId, { startedAt: new Date().toISOString() });
  }, [sprintId, sprintProgress?.startedAt, locked, setSprintProgress]);

  if (!sprint) {
    return (
      <NavShell screen="sprint" onNav={handleNav}>
        <div className="screen">
          <SkipLink />
          <Topbar crumb={[{ label: 'Studio', onClick: onLogo }]} onLogo={onLogo} />
          <main id="main-content" className="sprint-main" tabIndex={-1}>
            <div className="sprint-head-card">
              <h1 className="sprint-h1">Sprint not found: {sprintId}</h1>
            </div>
          </main>
        </div>
      </NavShell>
    );
  }

  const filtered = tickets.filter((t) => {
    const st = progress.tickets[t.id]?.status ?? 'todo';
    if (filter === 'all') return true;
    if (filter === 'done') return st === 'done';
    if (filter === 'open') return st === 'in-progress' || st === 'todo' || st === 'review';
    if (filter === 'master') return t.type === 'master' || t.type === 'mission';
    return true;
  });

  const closed = tickets.filter((t) => progress.tickets[t.id]?.status === 'done').length;
  const open = tickets.length - closed;
  const lastTouched = progress.last.ticketId;

  // dayLabel superseded by <SprintDayHeader /> render below · startedLabel
  // still consumed inline в meta-row для «started X month» display.
  const { startedLabel } = formatDayCounter(earliestStarted);

  // Sprint-level navigation: prev disabled (per prototype) · peek calls onPeek.
  const prevSprintOrd = sprint.ord - 1;
  const nextSprintOrd = sprint.ord + 1;
  const nextSprint = SPRINTS.find((s) => s.ord === nextSprintOrd);
  const prevSprintLabel = `Sprint ${prevSprintOrd.toString().padStart(2, '0')}`;
  const nextSprintLabel = nextSprint
    ? `Sprint ${nextSprintOrd.toString().padStart(2, '0')}`
    : null;
  const peekTarget = nextSprint?.id ?? null;

  return (
    <NavShell screen="sprint" onNav={handleNav}>
      <div className="screen">
        <SkipLink />
        {/* slot_finaltest_ux #4 · sprint-completion nudge → final test (first visit). */}
        {showTestModal && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ft-nudge-title"
            data-testid="sprint-test-nudge"
            onClick={dismissTestNudge}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.45)',
            }}
          >
            <div
              className="state-card passed"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: 440,
                margin: 16,
                borderRadius: 14,
                padding: '24px 24px 20px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
              }}
            >
              <h3 id="ft-nudge-title" style={{ marginTop: 0 }}>
                Все тикеты закрыты 🎉
              </h3>
              <p>
                Спринт пройден — можно перейти к <strong>финальному тесту</strong> и разблокировать
                Sprint Review.
              </p>
              <div className="ctas" style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-primary-state green"
                  data-testid="sprint-test-nudge-go"
                  onClick={() => {
                    dismissTestNudge();
                    onFinalTest?.(sprintId);
                  }}
                >
                  🎯 Перейти к тесту
                </button>
                <button
                  type="button"
                  className="btn-ghost-state"
                  data-testid="sprint-test-nudge-later"
                  onClick={dismissTestNudge}
                >
                  Отложить
                </button>
              </div>
            </div>
          </div>
        )}
        {/* slot_finaltest_ux #4 · subsequent visits → non-blocking toast (not modal). */}
        {showTestToast && (
          <div
            role="status"
            data-testid="sprint-test-toast"
            style={{
              position: 'fixed',
              left: '50%',
              bottom: 24,
              transform: 'translateX(-50%)',
              zIndex: 48,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              maxWidth: '92vw',
              padding: '10px 12px 10px 16px',
              borderRadius: 999,
              background: 'var(--ink, #1e2a44)',
              color: '#fff',
              boxShadow: '0 8px 28px rgba(0,0,0,0.32)',
              fontSize: 14,
            }}
          >
            <span>🎉 Спринт пройден — готов финальный тест.</span>
            <button
              type="button"
              data-testid="sprint-test-toast-go"
              onClick={() => {
                setNudge('off');
                onFinalTest?.(sprintId);
              }}
              style={{
                background: '#fff',
                color: 'var(--ink, #1e2a44)',
                border: 0,
                borderRadius: 999,
                padding: '5px 12px',
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: 600,
              }}
            >
              Перейти →
            </button>
            <button
              type="button"
              aria-label="Скрыть"
              data-testid="sprint-test-toast-close"
              onClick={() => setNudge('off')}
              style={{
                background: 'none',
                border: 0,
                color: 'inherit',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: 18,
                opacity: 0.75,
                lineHeight: 1,
                padding: '0 4px',
              }}
            >
              ×
            </button>
          </div>
        )}
        <Topbar
          crumb={[
            { label: 'Studio', onClick: onLogo },
            onSprints
              ? { label: 'Карта', onClick: onSprints }
              : { label: 'Dashboard', onClick: onDashboard },
            { label: `Sprint ${sprint.ord.toString().padStart(2, '0')}`, here: true },
          ]}
          onLogo={onLogo}
          /* slot_vocabulary_navbar (17 MAY 2026): Topbar's onOpenGlossary
             dropped — Glossary entry promoted to Sidebar foot via NavShell
             intercept (Q3=a). ExamHQ (ex-CockpitOverview · slot_s06_rework W5)
             keeps its in-context shortcut below — same callback prop chain
             (Sprint receives onOpenGlossary/onOpenFlashcards, forwards to ExamHQ). */
        />
        <main id="main-content" className="sprint-main" tabIndex={-1}>
          {demo && (
            <div className="demo-banner" role="status" style={{ gridColumn: '1 / -1' }}>
              <span className="demo-banner-tag">👁 DEMO</span>
              <span className="demo-banner-text">
                Режим предпросмотра — прогресс не сохраняется. Полный доступ откроется с покупкой курса.
              </span>
            </div>
          )}
          {/* slot_s06_rework W5 (11 JUN 2026): CockpitOverview → ExamHQ «штаб
              подготовки» (кокпит-первый вход · план по дням · слабые главы с
              дриллами · материалы). Мокап 04_DESIGN/mockup_s06_hq.html. */}
          {isS06 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <ExamHQ
                onStartMock={onStartMock}
                onOpenFlashcards={onOpenFlashcards ?? onOpenGlossary}
                onTicket={onTicket}
              />
            </div>
          )}
          <div className="sprint-head-card">
            {/* RISO Phase C · «← К карте курса» back link · мирно coexists
                с Topbar breadcrumb · prototype-fidelity per screens-sprint.jsx line 57. */}
            {onSprints && (
              <a
                className="sprint-back"
                onClick={onSprints}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSprints();
                  }
                }}
              >
                ← К карте курса
              </a>
            )}
            <div className="meta-row">
              <span className="pill epic">EPIC · {sprint.category}</span>
              <span className="pill active">
                <span className="pulse" />
                Active sprint
              </span>
              <span className="micro">
                SPRINT {sprint.ord.toString().padStart(2, '0')} / 18 · block: {sprint.block}
                {startedLabel && ` · ${startedLabel}`}
              </span>
              {/* Q-1 (CHAT_2B Genie adopt 16 MAY · helpers @ cfa3c1a · component
                  @ 7e8cdc0): «День N / 7» chip с sprintProgressInDays composite
                  helper · supersedes inline dayLabel display (formatDayCounter
                  retained for startedLabel above). */}
              <SprintDayHeader
                sprint={sprintProgress}
                ticketsInSprint={tickets.map((t) => progress.tickets[t.id] ?? {})}
              />
            </div>
            <h1 className="sprint-h1">
              <span className="accent">{sprint.category}</span> — {sprint.title.split('—')[1]?.trim() ?? sprint.title}
            </h1>
            <div className="goal">
              🎯 <strong>Цель спринта:</strong> {sprint.goal}
            </div>

            <div className="metrics-strip">
              <div className="metric">
                <div className="num">{tickets.length}</div>
                <div className="lab">тикетов</div>
              </div>
              <div className="metric done">
                <div className="num">{closed}</div>
                <div className="lab">закрыто</div>
              </div>
              <div className="metric todo">
                <div className="num">{open}</div>
                <div className="lab">осталось</div>
              </div>
              <div className="metric">
                <div className="num">~{Math.round(sprint.estimateMin)}</div>
                <div className="lab">мин всего</div>
              </div>
            </div>

            {/* RISO Phase C · sprint-level nav · 5-button layout per prototype.
                prev disabled visual · current «act» · peek calls onPeek · final + review primary. */}
            <div className="sprint-nav">
              <button
                type="button"
                className="snav-btn disabled"
                disabled
                aria-label={prevSprintOrd >= 1 ? `${prevSprintLabel} (предыдущий)` : 'Нет предыдущего спринта'}
              >
                ← {prevSprintOrd >= 1 ? prevSprintLabel : 'Sprint 00'}
              </button>
              <button type="button" className="snav-btn act" aria-current="page">
                ⏵ Sprint {sprint.ord.toString().padStart(2, '0')} · current
              </button>
              {/* Peek is a trial-only demo affordance (Phase 4) · full/admin progress
                  linearly, so the next sprint is just a disabled lock for them. */}
              {nextSprintLabel && peekTarget && canPeekSprint(progress, peekTarget, role) ? (
                <button
                  type="button"
                  className="snav-btn"
                  onClick={() => onPeek?.(peekTarget)}
                  disabled={!onPeek}
                  title={onPeek ? `Заглянуть в ${nextSprintLabel}` : 'Peek недоступен'}
                >
                  🔒 {nextSprintLabel} (демо)
                </button>
              ) : nextSprintLabel && peekTarget ? (
                <button type="button" className="snav-btn disabled" disabled>
                  🔒 {nextSprintLabel}
                </button>
              ) : (
                <button type="button" className="snav-btn disabled" disabled>
                  🔒 Последний спринт
                </button>
              )}
              <button
                type="button"
                className={`snav-btn primary ${finalTestUnlocked ? '' : 'disabled'}`}
                disabled={!finalTestUnlocked || !onFinalTest}
                onClick={() => onFinalTest?.(sprintId)}
                title={
                  !hasQuiz
                    ? 'Quiz bank в разработке'
                    : !requiredDone
                      ? `Закрой все Required тикеты (${requiredTickets.filter((t) => progress.tickets[t.id]?.status === 'done').length}/${requiredTickets.length})`
                      : 'Финальный тест спринта'
                }
              >
                📝 Финальный тест →
              </button>
              <button
                type="button"
                className={`snav-btn primary ${finalTestPassed ? '' : 'disabled'}`}
                disabled={!finalTestPassed || !onReview}
                onClick={() => onReview?.(sprintId)}
                title={finalTestPassed ? 'Sprint Review' : 'Сначала пройди финальный тест'}
              >
                🎉 Sprint Review →
              </button>
            </div>

            {finalTestUnlocked && !finalTestPassed && onFinalTest && (
              <div role="status" aria-live="polite" className="final-test-ready">
                <div className="copy">
                  <strong>🎯 Все Required тикеты закрыты!</strong>
                  <span>Финальный тест спринта разблокирован · открой когда готова.</span>
                </div>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => onFinalTest(sprintId)}
                >
                  📝 Открыть финальный тест →
                </button>
              </div>
            )}
          </div>

          <div>
            <div className="ticket-section">
              <div className="section-head">
                <span className="h">⊳ Тикеты в спринте</span>
                <span className="cnt">{tickets.length}</span>
                {viewMode === 'list' && (
                  <div className="filter">
                    {FILTERS.map((f) => (
                      <span
                        key={f.k}
                        className={filter === f.k ? 'act' : ''}
                        onClick={() => setFilter(f.k)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setFilter(f.k);
                          }
                        }}
                      >
                        {f.label}
                      </span>
                    ))}
                  </div>
                )}
                {/* SBM merge · view toggle · replaces static «📋 Канбан»
                    indicator + Block 1/2 fork. Visible in both blocks ·
                    aria-pressed pair · localStorage persisted (Genie OQ-1/2/3). */}
                <div
                  className="view-toggle"
                  role="group"
                  aria-label="Вид списка тикетов"
                  data-testid="view-toggle"
                  style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    gap: 2,
                    padding: 2,
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    aria-pressed={viewMode === 'list'}
                    data-testid="view-toggle-list"
                    style={{
                      fontFamily: 'Space Mono, monospace',
                      fontSize: 11,
                      letterSpacing: 0.3,
                      padding: '5px 10px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      background: viewMode === 'list' ? 'var(--accent-soft)' : 'transparent',
                      color: viewMode === 'list' ? 'var(--ink)' : 'var(--ink-soft)',
                      fontWeight: viewMode === 'list' ? 700 : 500,
                    }}
                  >
                    <span aria-hidden="true">☰</span> список
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('board')}
                    aria-pressed={viewMode === 'board'}
                    data-testid="view-toggle-board"
                    style={{
                      fontFamily: 'Space Mono, monospace',
                      fontSize: 11,
                      letterSpacing: 0.3,
                      padding: '5px 10px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      background: viewMode === 'board' ? 'var(--accent-soft)' : 'transparent',
                      color: viewMode === 'board' ? 'var(--ink)' : 'var(--ink-soft)',
                      fontWeight: viewMode === 'board' ? 700 : 500,
                    }}
                  >
                    <span aria-hidden="true">⊞</span> доска
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('overview')}
                    aria-pressed={viewMode === 'overview'}
                    data-testid="view-toggle-overview"
                    style={{
                      fontFamily: 'Space Mono, monospace',
                      fontSize: 11,
                      letterSpacing: 0.3,
                      padding: '5px 10px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      background: viewMode === 'overview' ? 'var(--accent-soft)' : 'transparent',
                      color: viewMode === 'overview' ? 'var(--ink)' : 'var(--ink-soft)',
                      fontWeight: viewMode === 'overview' ? 700 : 500,
                    }}
                  >
                    <span aria-hidden="true">▦</span> обзор
                  </button>
                </div>
              </div>
              {viewMode === 'overview' ? (
                // slot_board_overview (10 JUL · Genie): «обзор» как вкладка доски —
                // тот же bento-дашборд, что и на экране «Обзор» (курс-уровень сводка
                // без ухода со спринта). Экран «Обзор» в навигации остаётся отдельно.
                <div style={{ padding: '4px 2px 8px' }}>
                  <OverviewBento
                    progress={progress}
                    currentSprintOrd={overviewData.currentSprintOrd}
                    streakDays={overviewData.streakDays}
                    skills={overviewData.skills}
                    nearestMilestone={overviewData.nearestMilestone}
                    onSprint={() => setViewMode('board')}
                  />
                </div>
              ) : viewMode === 'board' ? (
                // SBM merge · Board rendered for both Block 1 & Block 2+ when
                // viewMode === 'board' · default Block 2+ behavior preserved via
                // loadInitialViewMode block-based fallback (OQ-2 Genie pick).
                <Board sprintId={sprintId} onTicket={onTicket} />
              ) : filtered.length === 0 ? (
                <div className="notebook-page" style={{ margin: 18 }}>
                  <div className="marker-h">пусто</div>
                  <div className="scribble">
                    В этом фильтре нет тикетов. Поменяй фильтр или возьми тикет из «Открытые».
                  </div>
                </div>
              ) : (
                filtered.map((t) => {
                  const st = progress.tickets[t.id]?.status ?? 'todo';
                  return (
                    <TicketRow
                      key={t.id}
                      ticket={t}
                      status={st}
                      isCurrent={t.id === lastTouched}
                      onClick={() => onTicket(t.id)}
                    />
                  );
                })
              )}
            </div>

            {/* Инлайн-бэклог домашек убран 24 JUN 2026 (Genie): домашки переехали
                в хаб «Твоя практика» (PracticeSpace), и секция в доске их дублировала.
                Компонент BacklogSection оставлен (юнит-тест empty-state), но не
                рендерится здесь. Доступ к домашкам — через нав-пункт «Практика». */}
          </div>

          {/* slot_board_overview P1.2 (3 JUL 2026 · Genie): правый рейл виджетов
              (Прогресс/Журнал/ISTQB/веха) убран — доска теперь во всю ширину, а
              эти виджеты переехали во вкладку «Обзор» (OverviewBento). `.sprint-main`
              стал одноколоночным (screens.css). no_content_loss: данные не пропали,
              а считаются честно в Обзоре. */}

          {isS06 && (
            <div style={{ gridColumn: '1 / -1', marginTop: 18 }}>
              <VoucherTable />
            </div>
          )}
        </main>
      </div>
    </NavShell>
  );
}

// ─── Sprint Backlog (Homework) section ─────────────────────────────────────
//
// Inline section under tickets. Renders 38 HW total across S01-S12:
// — Block 1 (S01-S06): 20 HW · foreman shipped 2 MAY · 6 sprints · 2-4 HW each.
// — Block 2 (S07-S12): 18 HW · slot HOMEWORK_CONTENT_ENGINEERING migrated
//   from 02_CONTENT/sprint_NN_content.md to homework.ts 18 MAY · 6 sprints ·
//   3 HW each (~90 min total per sprint).
// For S13-S18 (Block 3+4) — empty-state remains: sprint content not yet
// authored, so HW gap unknown · default «нет домашки» pending Block 3/4
// content authoring.
//
// Status model: 3-state toggle (Genie Q3=(а)) · `locked → in-progress → done`
// persisted via `setHomeworkStatus` (ProgressContext extension). Click cycles
// through states.

interface BacklogProgress {
  homework?: Record<string, { status: HomeworkStatus }>;
}

interface BacklogSectionProps {
  sprintId: string;
  homework: ReadonlyArray<{
    id: string;
    code: string;
    title: string;
    icon: string;
    estimateMin: number;
    priority: string;
    description: string;
    prompt: string;
  }>;
  progress: BacklogProgress;
  /**
   * Status mutation kept для backwards-compat и для closed-state cycle-back option
   * (currently unused в render but preserved as API surface). Primary status flow
   * since HW.3 (18 MAY 2026): «взять» button navigates → Ticket homework variant ·
   * student presses «Сдать» там, который mutates status to 'done'.
   */
  onSetStatus: (homeworkId: string, status: HomeworkStatus) => void;
  /**
   * Navigate к Ticket homework variant · added HW.3 (18 MAY 2026).
   * Ticket.tsx resolves HW-* code via adapter and renders homework branch
   * with full prompt + journal + «Сдать» CTA.
   */
  onOpenHomework: (homeworkCode: string) => void;
}

// HW.3 (18 MAY 2026) · «взять» — first open, «продолжить» — resume in-progress
// homework, «✓ открыть» — re-open done homework для read-only review per Genie pick (а).
const HOMEWORK_STATUS_LABEL: Record<HomeworkStatus, string> = {
  locked: 'взять',
  'in-progress': 'продолжить',
  done: '✓ открыть',
};

// Exported for unit testing (empty-state coverage без full Sprint render).
export function BacklogSection({ sprintId, homework, progress, onSetStatus, onOpenHomework }: BacklogSectionProps) {
  // onSetStatus preserved для future extensibility (re-open closed homework cycle).
  // No-op reference to satisfy TS strict unused-arg check.
  void onSetStatus;
  const [collapsed, setCollapsed] = useState(false);
  const bodyId = useId();
  const toggle = () => setCollapsed((c) => !c);
  const handleKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };
  const chevron = collapsed ? '▸' : '▾';

  if (homework.length === 0) {
    return (
      <div className="ticket-section" style={{ marginTop: 18 }} data-testid="backlog-section-empty">
        <div
          role="button"
          tabIndex={0}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          onClick={toggle}
          onKeyDown={handleKey}
          data-testid="backlog-toggle"
          className="section-head backlog-toggle"
        >
          <span className="h">📋 Sprint Backlog · домашка</span>
          <span className="cnt">0</span>
          <span
            aria-hidden="true"
            style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--ink-mute)', width: 16, textAlign: 'center' }}
          >
            {chevron}
          </span>
        </div>
        {!collapsed && (
          <div id={bodyId} className="notebook-page" style={{ margin: 18 }}>
            <div className="marker-h">пусто</div>
            <div className="scribble">
              В этом спринте нет домашки · вся практика в тикетах.
            </div>
          </div>
        )}
      </div>
    );
  }

  const doneCount = homework.filter(
    (h) => progress.homework?.[h.id]?.status === 'done',
  ).length;

  return (
    <div className="ticket-section" style={{ marginTop: 18 }} data-testid="backlog-section">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-controls={bodyId}
        onClick={toggle}
        onKeyDown={handleKey}
        data-testid="backlog-toggle"
        className="section-head backlog-toggle"
      >
        <span className="h">📋 Sprint Backlog · домашка</span>
        <span className="cnt">{homework.length}</span>
        <span
          className="cnt"
          style={{ marginLeft: 'auto' }}
          aria-label={`Домашек закрыто: ${doneCount} из ${homework.length}`}
        >
          {doneCount}/{homework.length}
        </span>
        <span
          aria-hidden="true"
          style={{ fontSize: 14, color: 'var(--ink-mute)', width: 16, textAlign: 'center' }}
        >
          {chevron}
        </span>
      </div>
      {!collapsed && (
      <div id={bodyId} role="list" aria-label={`Домашка спринта ${sprintId}`}>
        {homework.map((hw) => {
          const status: HomeworkStatus = progress.homework?.[hw.id]?.status ?? 'locked';
          return (
            <div
              key={hw.id}
              role="listitem"
              data-testid={`hw-card-${hw.id}`}
              data-status={status}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto auto auto',
                gap: 12,
                alignItems: 'center',
                padding: '12px 18px',
                borderBottom: '1px solid var(--border)',
                opacity: status === 'done' ? 0.7 : 1,
              }}
            >
              <span
                aria-hidden="true"
                style={{ fontSize: 20, lineHeight: 1, width: 28, textAlign: 'center' }}
              >
                {hw.icon}
              </span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: 'Space Mono, monospace',
                    fontSize: 10.5,
                    color: 'var(--ink-mute)',
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}
                >
                  {hw.code}
                </div>
                <div
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    textDecoration: status === 'done' ? 'line-through' : 'none',
                  }}
                >
                  {hw.title}
                </div>
                <div
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 12,
                    color: 'var(--ink-soft)',
                    marginTop: 4,
                    lineHeight: 1.4,
                  }}
                >
                  {hw.description}
                </div>
              </div>
              <span
                className="pri-pill"
                style={{
                  fontFamily: 'Space Mono, monospace',
                  fontSize: 10,
                  padding: '3px 8px',
                  borderRadius: 50,
                  background: 'var(--surface-2)',
                  color: 'var(--ink-soft)',
                  border: '1px solid var(--border)',
                }}
                aria-label={`Приоритет ${hw.priority}`}
              >
                {hw.priority}
              </span>
              <span
                style={{
                  fontFamily: 'Space Mono, monospace',
                  fontSize: 11,
                  color: 'var(--ink-mute)',
                  whiteSpace: 'nowrap',
                }}
                aria-label={`${hw.estimateMin} минут`}
              >
                {hw.estimateMin}м
              </span>
              <button
                type="button"
                className="snav-btn"
                onClick={() => onOpenHomework(hw.code)}
                data-testid={`hw-open-${hw.id}`}
                aria-label={`Открыть домашку «${hw.title}» (статус: ${status})`}
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: 12,
                  padding: '6px 12px',
                  background: status === 'done' ? 'var(--accent-soft)' : 'var(--surface)',
                  borderColor: status === 'done' ? 'var(--accent)' : 'var(--border)',
                  color: status === 'done' ? 'var(--ink)' : 'var(--ink-soft)',
                  fontWeight: status === 'done' ? 700 : 500,
                }}
              >
                {HOMEWORK_STATUS_LABEL[status]}
              </button>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
